/**
 * Site-wide JSON-LD structured data helpers.
 * Returns plain JS objects ready to be passed to usePageMeta({ jsonLd: ... }).
 *
 * Production canonical: https://hopechest.organizedhope.online
 */

export const SITE_URL = "https://hopechest.organizedhope.online";
export const SITE_NAME = "Hope Chest";
export const ORG_NAME = "Organized Hope";

/** Organization — emit on Landing only (site identity). */
export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": ORG_NAME,
  "alternateName": SITE_NAME,
  "url": SITE_URL,
  "logo": `${SITE_URL}/hope-chest-logo.png`,
  "description": "Hope Chest is a digital binder for family caregivers — built by a caregiver to help families organize medical, legal, financial, and daily care details for the people they love.",
  "founder": { "@type": "Person", "name": "Michael" },
  "sameAs": [
    "https://organizedhope.com",
    "https://organizedhope.online",
  ],
  "contactPoint": [{
    "@type": "ContactPoint",
    "contactType": "customer support",
    "email": "support@organizedhope.online",
    "availableLanguage": ["English"],
  }],
});

/** WebSite with sitelink search box. Emit on Landing. */
export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_NAME,
  "url": SITE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/resources?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/** SoftwareApplication — emit on Landing & Pricing so Google sees Hope Chest as a SaaS product. */
export const softwareAppJsonLd = ({ monthlyPrice = "14.99", currency = "USD" } = {}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Hope Chest",
  "applicationCategory": "HealthApplication",
  "operatingSystem": "Web, iOS, Android",
  "description": "A digital binder for family caregivers — organize medications, appointments, contacts, documents, and daily care notes in one secure place.",
  "url": SITE_URL,
  "offers": [
    {
      "@type": "Offer",
      "name": "Free",
      "price": "0",
      "priceCurrency": currency,
      "description": "Free forever for one Hope Chest. Track medications, appointments, contacts, and care notes for one loved one.",
    },
    {
      "@type": "Offer",
      "name": "Premium",
      "price": monthlyPrice,
      "priceCurrency": currency,
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": monthlyPrice,
        "priceCurrency": currency,
        "unitText": "MONTH",
      },
      "description": "Unlimited Hope Chests, family sharing, SMS reminders, calendar sync, printable PDF binder, and weekly care-team reports.",
    },
  ],
});

/** FAQPage — emit on Pricing. Pass the FAQ_ITEMS array. */
export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": (items || []).map((it) => ({
    "@type": "Question",
    "name": it.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": it.a,
    },
  })),
});

/** DefinedTermSet — emit on Resources. Pass the GLOSSARY array. */
export const glossaryJsonLd = (terms) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  "name": "Caregiving Glossary",
  "description": "Plain-language definitions of medical, legal, and care-coordination vocabulary family caregivers will encounter.",
  "url": `${SITE_URL}/resources`,
  "hasDefinedTerm": (terms || []).map((t) => ({
    "@type": "DefinedTerm",
    "name": t.term,
    "description": t.def,
    "inDefinedTermSet": `${SITE_URL}/resources#glossary`,
  })),
});

/** ItemList of guides — emit on Resources. Pass the GUIDES array. */
export const guidesItemListJsonLd = (guides) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Caregiver Guides",
  "description": "Practical guides for family caregivers — medications, doctor visits, legal documents, family coordination, and more.",
  "url": `${SITE_URL}/resources`,
  "itemListElement": (guides || []).map((g, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "item": {
      "@type": "Article",
      "headline": g.title,
      "description": g.summary,
      "url": `${SITE_URL}/resources#${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`,
      "author": { "@type": "Organization", "name": ORG_NAME },
      "publisher": {
        "@type": "Organization",
        "name": ORG_NAME,
        "logo": { "@type": "ImageObject", "url": `${SITE_URL}/hope-chest-logo.png` },
      },
    },
  })),
});

/** BreadcrumbList helper. Pass [{name, url}, ...]. */
export const breadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": (crumbs || []).map((c, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": c.name,
    "item": c.url.startsWith("http") ? c.url : `${SITE_URL}${c.url}`,
  })),
});
